import { safe_get } from "../../../../report-visuals";
import { facetResultFromExecuteResponse } from "./facet-etl-execute-helpers";
import {
    resolveEtlNodeFromDocument,
    writeEtlNodeToDocument,
} from "./facet-etl-document-helpers";
import { FACET_ETL_DS_TYPE_ESINDEX } from "./facet-form-config";
import { extractFacetTableData } from "./facet-table-helpers";

const ROW_HEADER_FIELDS = ["row_headers", "row_header"] as const;

export interface NormalizeKeyDataSourceHint {
    esIndex?: string | null;
    sourceId?: number | null;
}

export interface ResolveNormalizeKeyOptionsContext {
    keyRequestedFromApi?: boolean;
}

function normalizeRowHeaderValues(list: unknown[]): string[] {
    return [
        ...new Set(
            list
                .map((x) => {
                    if (typeof x === "string") return x.trim();
                    if (x != null && typeof x === "object") {
                        const obj = x as Record<string, unknown>;
                        const v = obj.key ?? obj.name ?? obj.label ?? obj.value;
                        return typeof v === "string" ? v.trim() : "";
                    }
                    return String(x ?? "").trim();
                })
                .filter(Boolean)
        ),
    ];
}

function rowHeaderCandidates(data: unknown): unknown[] {
    if (data == null || typeof data !== "object") return [];
    const obj = data as Record<string, unknown>;
    const facetResult = obj.facet_result;
    const out: unknown[] = [];
    for (const field of ROW_HEADER_FIELDS) {
        out.push(obj[field], safe_get(obj, `facet_result.${field}`, undefined));
        if (facetResult != null && typeof facetResult === "object" && !Array.isArray(facetResult)) {
            out.push((facetResult as Record<string, unknown>)[field]);
        }
    }
    return out;
}

function valuesFromNestedObject(raw: unknown): string[] | null {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
    const obj = raw as Record<string, unknown>;
    for (const field of ["values", "headers", "labels", "items", "value"] as const) {
        const v = obj[field];
        if (Array.isArray(v)) return normalizeRowHeaderValues(v);
        if (typeof v === "string" && v.trim()) return [v.trim()];
    }
    return null;
}

function listForKeyedMap(raw: unknown, hint: string): string[] | null {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
    const map = raw as Record<string, unknown>;
    const exact = map[hint];
    if (Array.isArray(exact)) return normalizeRowHeaderValues(exact);
    const nestedExact = valuesFromNestedObject(exact);
    if (nestedExact?.length) return nestedExact;

    const lower = hint.toLowerCase();
    for (const [k, v] of Object.entries(map)) {
        if (k.trim().toLowerCase() !== lower) continue;
        if (Array.isArray(v)) return normalizeRowHeaderValues(v);
        const nested = valuesFromNestedObject(v);
        if (nested?.length) return nested;
    }
    return null;
}

function listFromKeyedEntries(raw: unknown, hint: string): string[] | null {
    if (!Array.isArray(raw)) return null;
    const lower = hint.toLowerCase();
    for (const item of raw) {
        if (item == null || typeof item !== "object" || Array.isArray(item)) continue;
        const entry = item as Record<string, unknown>;
        const entryKey = entry.key ?? entry.name ?? entry.field ?? entry.header;
        if (typeof entryKey !== "string" || entryKey.trim().toLowerCase() !== lower) continue;
        const vals = entry.values ?? entry.headers ?? entry.labels ?? entry.items ?? entry.value;
        if (Array.isArray(vals)) return normalizeRowHeaderValues(vals);
        if (typeof vals === "string" && vals.trim()) return [vals.trim()];
    }
    return null;
}

export function resolveNormalizeFieldKey(input: string, knownKeys: string[]): string {
    const trimmed = input.trim();
    if (!trimmed) return "";
    if (knownKeys.includes(trimmed)) return trimmed;

    const lower = trimmed.toLowerCase();
    const exactCi = knownKeys.find((k) => k.toLowerCase() === lower);
    if (exactCi) return exactCi;

    const prefixMatches = knownKeys.filter((k) => k.toLowerCase().startsWith(lower));
    if (prefixMatches.length === 1) return prefixMatches[0];

    const containsMatches = knownKeys.filter((k) => k.toLowerCase().includes(lower));
    if (containsMatches.length === 1) return containsMatches[0];

    return trimmed;
}

export function ensureNormalizeProbeDataSource(
    formJson: Record<string, unknown>,
    hint?: NormalizeKeyDataSourceHint
): Record<string, unknown> {
    const etl = resolveEtlNodeFromDocument(formJson);
    if (!etl) return formJson;

    const ds = { ...((etl.ds as Record<string, unknown> | undefined) ?? {}) };
    const currentIndex =
        typeof ds.esindex_name === "string"
            ? ds.esindex_name.trim()
            : ds.esindex_name != null
              ? String(ds.esindex_name).trim()
              : "";

    const esIndex = currentIndex || hint?.esIndex?.trim() || "";
    if (esIndex) {
        ds.esindex_name = esIndex;
        ds.type = FACET_ETL_DS_TYPE_ESINDEX;
    }
    if (hint?.sourceId != null && Number.isFinite(hint.sourceId)) {
        ds.id = hint.sourceId;
    }

    return writeEtlNodeToDocument(formJson, { ...etl, ds });
}

export function buildNormalizeKeyProbeEtlDoc(
    formJson: Record<string, unknown>,
    normalizeKey: string,
    dataSourceHint?: NormalizeKeyDataSourceHint
): Record<string, unknown> | null {
    const key = normalizeKey.trim();
    if (!key) return null;

    const withDs = ensureNormalizeProbeDataSource(formJson, dataSourceHint);
    const etl = resolveEtlNodeFromDocument(withDs);
    if (!etl) return null;

    const ds = (etl.ds as Record<string, unknown> | undefined) ?? {};
    const esIndex =
        typeof ds.esindex_name === "string"
            ? ds.esindex_name.trim()
            : String(ds.esindex_name ?? "").trim();
    if (!esIndex) return null;

    const probeEtl: Record<string, unknown> = {
        ...etl,
        ops: [
            {
                type: "pivot",
                mode: "blacklist",
                runon: "esnode",
                "data-type": "str",
                row_key: key,
                col_key: "",
                sort_by: "value",
                sort_order: "desc",
                row_transform: {},
                col_transform: {},
                col_blacklist: [],
                col_whitelist: [],
                row_blacklist: [],
                row_whitelist: [],
                include_total_cols: false,
                include_total_rows: false,
                row_sort: {},
                column_sort: {},
            },
        ],
    };

    if (withDs.etl != null && typeof withDs.etl === "object") {
        return { ...withDs, etl: probeEtl };
    }
    return { ...withDs, ...probeEtl };
}

export function resolveNormalizeKeyValueOptionsKeyed(data: unknown, keyHint: string): string[] {
    const hint = keyHint.trim();
    if (!hint || data == null) return [];

    for (const raw of rowHeaderCandidates(data)) {
        const fromMap = listForKeyedMap(raw, hint);
        if (fromMap?.length) return fromMap;
        const fromEntries = listFromKeyedEntries(raw, hint);
        if (fromEntries?.length) return fromEntries;
    }
    return [];
}

export function resolveNormalizeKeyValueOptions(
    data: unknown,
    keyHint: string,
    ctx?: ResolveNormalizeKeyOptionsContext
): string[] {
    const hint = keyHint.trim();
    if (!hint || data == null) return [];

    if (ctx?.keyRequestedFromApi) {
        for (const raw of rowHeaderCandidates(data)) {
            if (Array.isArray(raw)) {
                const direct = normalizeRowHeaderValues(raw);
                if (direct.length > 0) return direct;
            }
        }
    }
    return resolveNormalizeKeyValueOptionsKeyed(data, hint);
}

export function resolveProbePivotRowLabels(data: unknown): string[] {
    if (data == null || typeof data !== "object") return [];
    const facetResult = facetResultFromExecuteResponse(data as Record<string, unknown>);
    const table = extractFacetTableData(facetResult);
    if (table && table.rows.length > 0 && table.headers.length > 0) {
        const labelCol = table.headers[0];
        const labels = table.rows
            .map((row) => String(row[labelCol] ?? "").trim())
            .filter((label) => label && label.toLowerCase() !== "total");
        if (labels.length > 0) return [...new Set(labels)];
    }

    const rows = facetResult.rows;
    if (!Array.isArray(rows)) return [];

    const labels: string[] = [];
    for (const row of rows) {
        if (Array.isArray(row) && row.length > 0) {
            const label = String(row[0] ?? "").trim();
            if (label && label.toLowerCase() !== "total") labels.push(label);
        }
    }
    return [...new Set(labels)];
}
